import {
  EncounterClass,
  EncounterService,
  getPeriodDayWithPausePeriod,
  getPeriodDurationDays,
  type Period,
} from '@quromedical/fhir-common';
import { parseDate } from '@quromedical/utils';
import { format, isBefore, isEqual, parseISO } from 'date-fns';
import type { EncounterStatusHistory } from 'fhir/r4';
import { strings } from 'strings';

export const admissionServiceDisplay: Record<EncounterService, string> = {
  [EncounterService.HospitalAtHome]: strings.AdmissionServiceHospitalAtHome,
  [EncounterService.TCP]: strings.AdmissionServiceTCP,
  [EncounterService.RemotePatientMonitoring]:
    strings.AdmissionServiceRemotePatientMonitoring,
  [EncounterService.Arthroplasty]: strings.AdmissionServiceArthroplasty,
  [EncounterService.HealingHands]: strings.AdmissionServiceHealingHands,
  [EncounterService.InHospitalMonitoring]:
    strings.AdmissionServiceInHospitalMonitoring,
  [EncounterService.HospitalAtHomeUpgrade]:
    strings.AdmissionServiceHospitalAtHomeUpgrade,
  [EncounterService.SPHERE]: strings.AdmissionServiceSPHERE,
};

export const admissionServiceShortDisplay: Record<EncounterService, string> = {
  [EncounterService.HospitalAtHome]:
    strings.AdmissionServiceShortHospitalAtHome,
  [EncounterService.TCP]: strings.AdmissionServiceShortTCP,
  [EncounterService.RemotePatientMonitoring]:
    strings.AdmissionServiceShortRemotePatientMonitoring,
  [EncounterService.Arthroplasty]: strings.AdmissionServiceShortArthroplasty,
  [EncounterService.HealingHands]: strings.AdmissionServiceShortHealingHands,
  [EncounterService.InHospitalMonitoring]:
    strings.AdmissionServiceShortInHospitalMonitoring,
  [EncounterService.HospitalAtHomeUpgrade]:
    strings.AdmissionServiceShortHospitalAtHomeUpgrade,
  [EncounterService.SPHERE]: strings.AdmissionServiceShortSPHERE,
};

export const admissionClassDisplay: Record<EncounterClass, string> = {
  [EncounterClass.Home]: strings.AdmissionClassHome,
  [EncounterClass.Hospital]: strings.AdmissionClassHospital,
};

export const admissionStatusDisplay: Record<fhir4.Encounter['status'], string> =
  {
    'entered-in-error': strings.EncounterStatusEnteredInError,
    'in-progress': strings.EncounterStatusInProgress,
    arrived: strings.EncounterStatusArrived,
    cancelled: strings.EncounterStatusCancelled,
    finished: strings.EncounterStatusFinished,
    onleave: strings.EncounterStatusOnLeave,
    planned: strings.EncounterStatusPlanned,
    triaged: strings.EncounterStatusTriaged,
    unknown: strings.EncounterStatusUnknown,
  };

type FutureDisplayMode = 'date' | 'message';

export const formatDisplayDate = (date?: string | number): string => {
  const parsed = parseDate(date);
  return parsed ? format(parsed, 'dd MMM yyyy') : 'Invalid date';
};

export const getPeriodDayDisplay = (
  period?: Period,
  futureDisplayMode?: FutureDisplayMode,
  admissionStatus?: fhir4.Encounter['status'],
): number | string | undefined => {
  if (!period?.start) {
    return undefined;
  }
  const periodStart = parseISO(period.start);
  const now = new Date();
  const isAdmissionOverdue =
    isBefore(periodStart, now) || isEqual(now, periodStart);

  if (admissionStatus === 'planned' && isAdmissionOverdue) {
    return 0;
  }

  const periodDuration = getPeriodDurationDays(period);

  if (typeof periodDuration === 'undefined') {
    return undefined;
  }

  if (periodDuration > 0) {
    return periodDuration;
  }

  const absoluteDuration = Math.abs(periodDuration);

  if (futureDisplayMode === 'date') {
    return formatDisplayDate(period.start);
  }

  const pluralSuffix = absoluteDuration > 1 ? 's' : '';
  return `Admission in ${absoluteDuration} day${pluralSuffix}`;
};
type PlanPeriodDisplayProp = {
  service?: EncounterService;
  period?: Period;
  statusHistory?: EncounterStatusHistory[];
  futureDisplayMode?: FutureDisplayMode;
  status?: fhir4.Encounter['status'];
};

export const getPlanPeriodDisplay = ({
  service,
  period,
  statusHistory = [],
  futureDisplayMode = 'message',
  status,
}: PlanPeriodDisplayProp): string => {
  if (!service) {
    return strings.AdmissionServiceNoneText;
  }

  const serviceDisplay = service && admissionServiceShortDisplay[service];

  const hasPausedHistory = statusHistory.some(
    (history) => history.status === 'onleave',
  );

  const dayDisplay = hasPausedHistory
    ? getPeriodDayWithPausePeriod(statusHistory, period)
    : getPeriodDayDisplay(period, futureDisplayMode, status);

  if (dayDisplay === undefined || dayDisplay === null) {
    return serviceDisplay;
  }

  if (typeof dayDisplay === 'number') {
    return `${serviceDisplay} (${strings.AdmissionDurationPrefixDay} ${dayDisplay})`;
  }

  return `${serviceDisplay} (${dayDisplay})`;
};

/**
 * Get the plan display for a note to display which date of the plan a note was created.
 *
 * This is done such that the date duration will be calculated based on the period of the
 */
export const getNotePlanDisplay = (
  noteDate?: string,
  admissionService?: EncounterService,
  admissionPeriod?: Period,
  statusHistory: EncounterStatusHistory[] = [],
): string | undefined => {
  const hasAllData = noteDate && admissionService && admissionPeriod?.start;

  if (!hasAllData) {
    return undefined;
  }

  return getPlanPeriodDisplay({
    service: admissionService,
    period: {
      start: admissionPeriod.start,
      end: noteDate,
    },
    statusHistory,
    futureDisplayMode: 'date',
  });
};
